import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import { apiRequest, formatDate } from '../../utils';
import './MemberHome.css';
import '../storefront/Storefront.css';

/* Bravos MEMBER Lab checkout (/book/:eventId) — a dedicated in-app booking journey for a
   signed-in member: pre-filled contact + address, a voucher field and Stripe payment, WITHOUT
   the hop to the public storefront. It reuses the shared storefront checkout endpoint (voucher
   / Stripe / ticket issuance / individual-membership mint), so the only new thing here is the
   pre-filled, single-seat screen. After payment the member returns to /member-home. */

const Field = ({ label, children }) => (
  <div className="auth__field">
    <label className="auth__label">{label}</label>
    <div className="auth__input-wrap">{children}</div>
  </div>
);

const money = (currency, value) => `${currency} ${Number(value).toFixed(2)}`;

// A voucher handed to the member via an "invite a friend with my voucher" link is stashed in
// sessionStorage by the invite-accept page (it survives the redirect, unlike a query param that
// usePersistUrlParams would strip). Read + consume it once, only for the matching Lab.
const readPendingVoucher = (eventId) => {
  try {
    const raw = window.sessionStorage.getItem('bravosBookVoucher');
    if (!raw) return null;
    const { event_id: e, code } = JSON.parse(raw);
    if (code && String(e) === String(eventId)) {
      window.sessionStorage.removeItem('bravosBookVoucher');
      return code;
    }
  } catch (_) { /* ignore malformed / unavailable storage */ }
  return null;
};

const MemberCheckout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { eventId } = useParams();

  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [rateId, setRateId] = useState(null);
  const [voucher, setVoucher] = useState('');
  const [voucherApplied, setVoucherApplied] = useState(null); // {code, discount} once previewed
  const [voucherError, setVoucherError] = useState(null);
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [autoCode, setAutoCode] = useState(null);      // a code to auto-apply once the rate loads
  const [prefilled, setPrefilled] = useState(false);   // a voucher was pre-filled for the member
  const [terms, setTerms] = useState(false);
  const [addr, setAddr] = useState({ street: '', number: '', box: '', zip: '', city: '', country: '' });
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);

  const [idempotencyKey] = useState(() =>
    (window.crypto && window.crypto.randomUUID)
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    (async () => {
      try {
        const d = await apiRequest('GET', `/bravos/lab/${eventId}/catalog`);
        setCatalog(d);
        const p = d.prefill || {};
        setAddr({
          street: p.street || '', number: p.number || '', box: p.box || '',
          zip: p.zip || '', city: p.city || '', country: p.country || '',
        });
        const defaultRate = d.products && d.products.length ? d.products[0].rate_id : null;
        // Pre-fill a voucher: one this member HOLDS (server-computed, apply straight away) or a
        // code handed to them via an "invite a friend with my voucher" link (auto-preview once).
        const held = d.held_voucher;
        const pending = readPendingVoucher(eventId);
        if (held && held.code) {
          setVoucher(held.code);
          setRateId(held.rate_id || defaultRate);
          setVoucherApplied({ code: held.code, discount: held.discount });
          setPrefilled(true);
        } else {
          if (defaultRate) setRateId(defaultRate);
          if (pending) { setVoucher(pending); setAutoCode(pending); setPrefilled(true); }
        }
      } catch (err) {
        setError(t(err.message, { defaultValue: err.message }));
      } finally {
        setLoading(false);
      }
    })();
  }, [eventId, t]);

  const product = useMemo(
    () => (catalog && catalog.products ? catalog.products.find((p) => p.rate_id === rateId) : null),
    [catalog, rateId],
  );

  const setAddrField = (k) => (e) => setAddr((a) => ({ ...a, [k]: e.target.value }));

  // Editing the code, or switching rate, invalidates a previewed discount — the member
  // must re-apply so the total never shows a stale/mismatched voucher effect.
  const clearVoucherPreview = () => { setVoucherApplied(null); setVoucherError(null); };
  const onVoucherChange = (e) => { setVoucher(e.target.value); setPrefilled(false); setAutoCode(null); clearVoucherPreview(); };
  const chooseRate = (id) => { setRateId(id); clearVoucherPreview(); };

  const applyVoucher = async (codeArg) => {
    const code = (typeof codeArg === 'string' ? codeArg : voucher).trim();
    if (!code || !product) return;
    setVoucherLoading(true);
    setVoucherError(null);
    try {
      const d = await apiRequest('POST', `/bravos/lab/${eventId}/voucher-preview`,
        { code, rate_id: product.rate_id });
      if (d && d.ok && d.discount > 0) {
        setVoucherApplied({ code: d.code, discount: d.discount });
      } else {
        setVoucherApplied(null);
        // The endpoint answers with the translation key for the real reason (api/voucher_errors.py);
        // `voucher_limit` makes "already used" say how often the code may be used.
        setVoucherError(t((d && d.error) || 'voucherInvalid',
                          { count: d?.voucher_limit, defaultValue: t('voucherInvalid') }));
      }
    } catch (err) {
      setVoucherApplied(null);
      setVoucherError(t(err.message, { count: err.data?.voucher_limit, defaultValue: t('voucherInvalid') }));
    } finally {
      setVoucherLoading(false);
    }
  };

  // Auto-apply a code handed to the member via an invite link, once the rate is loaded.
  useEffect(() => {
    if (product && autoCode && !voucherApplied && !voucherLoading) {
      const code = autoCode;
      setAutoCode(null);
      applyVoucher(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, autoCode]);

  const basePrice = product ? (product.effective_price ?? product.unit_price) : 0;
  const discount = voucherApplied ? Math.min(voucherApplied.discount, basePrice) : 0;
  const netPrice = Math.max(0, basePrice - discount);
  const vatAmount = catalog && catalog.with_vat && product
    ? netPrice * (Number(product.vat_rate) || 0) / 100 : 0;
  const total = netPrice + vatAmount;

  const book = async (e) => {
    e.preventDefault();
    if (!product) return;
    if (!addr.street || !addr.zip || !addr.city || !addr.country) {
      setError(t('fillRequiredFields'));
      return;
    }
    if (catalog.terms_url && !terms) {
      setError(t('fillRequiredFields'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const p = catalog.prefill || {};
      const data = await apiRequest('POST', '/storefront/checkout', {
        event_id: eventId,
        mode: 'full',
        basket: [{ rate_id: product.rate_id, qty: 1 }],
        idempotency_key: idempotencyKey,
        slug: catalog.slug,
        first_name: p.first_name, last_name: p.last_name, email: p.email, mobile: p.mobile,
        ...addr,
        voucher_code: voucher.trim(),
        terms_accepted: terms,
      });
      if (data.checkout_url) {
        // Online payment: pay on Stripe's hosted page; the success_url returns to the
        // storefront confirmation, which sends a Bravos member on to /member-home.
        window.location.href = data.checkout_url;
        return;
      }
      // No online payment (proforma): the booking is done and the membership minted —
      // straight back to the member home.
      navigate('/member-home');
    } catch (err) {
      setError(t(err.message, { defaultValue: err.message }));
    } finally {
      setSubmitting(false);
    }
  };

  // A member with a Lab ticket left spends it here instead of paying — the same endpoint the
  // member home's Register button uses (own pool first, then the company's).
  const spendTicket = () => {
    if (claiming) return;
    setClaiming(true);
    setError(null);
    apiRequest('POST', '/bravos/claim-seat', { event_id: eventId })
      .then(() => setClaimed(true))
      .catch((err) => setError(t(err.message, { defaultValue: t('claimFailed') })))
      .finally(() => setClaiming(false));
  };

  const seat = (catalog && catalog.seat) || {};

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/member-home" />
        <Title my="1" className="mt-3">
          <Icon name="ticket" theme="primary" className="me-2" />
          {t('bookLab')}
        </Title>

        {loading ? (
          <span className="auth__spinner" />
        ) : (claimed || seat.registered) ? (
          <>
            {catalog.event && <p className="bravos-q__hint">{catalog.event.name}</p>}
            <p className="pe-bravos-registered">
              <Icon name="check" /> {claimed ? t('seatClaimed') : t('youAreRegistered')}
            </p>
            <button type="button" className="bravos-submit" onClick={() => navigate('/member-home')}>
              {t('backToHome')}
            </button>
          </>
        ) : seat.claimable ? (
          <>
            {catalog.event && (
              <p className="bravos-q__hint">
                {catalog.event.name}
                {catalog.event.date ? ` · ${formatDate(catalog.event.date)}` : ''}
                {catalog.event.location_name ? ` · ${catalog.event.location_name}` : ''}
              </p>
            )}
            <p>{t('useLabTicketHint')}</p>
            {error && <p className="text-danger">{error}</p>}
            <button type="button" className="bravos-submit" onClick={spendTicket} disabled={claiming}>
              {claiming ? <span className="auth__spinner" /> : <Icon name="ticket" />}
              {t('useLabTicket')}
            </button>
          </>
        ) : !catalog || !catalog.products || !catalog.products.length ? (
          <p className="text-muted mb-0">{error || t('shopUnavailable', { defaultValue: t('storefrontNotFound') })}</p>
        ) : (
          <form onSubmit={book}>
            {catalog.event && (
              <p className="bravos-q__hint">
                {catalog.event.name}
                {catalog.event.date ? ` · ${formatDate(catalog.event.date)}` : ''}
                {catalog.event.location_name ? ` · ${catalog.event.location_name}` : ''}
              </p>
            )}

            {/* Ticket choice (usually one). */}
            {catalog.products.map((p) => (
              <label key={p.rate_id} className="storefront__summary" style={{ display: 'block', cursor: 'pointer' }}>
                <div className="storefront__product-head">
                  <span className="storefront__product-name">
                    {catalog.products.length > 1 && (
                      <input
                        type="radio"
                        name="rate"
                        checked={rateId === p.rate_id}
                        onChange={() => chooseRate(p.rate_id)}
                        className="me-2"
                      />
                    )}
                    {p.name}
                  </span>
                  <span className="storefront__product-price">
                    {money(catalog.currency, p.effective_price ?? p.unit_price)}
                  </span>
                </div>
              </label>
            ))}

            {product && (discount > 0 || catalog.with_vat) && (
              <div className="storefront__summary">
                {discount > 0 && (
                  <div className="storefront__product-head">
                    <span>{t('voucherApplied')} ({voucherApplied.code})</span>
                    <span>− {money(catalog.currency, discount)}</span>
                  </div>
                )}
                {catalog.with_vat && (
                  <div className="storefront__product-head">
                    <span>{t('vat')} ({Number(product.vat_rate)}%)</span>
                    <span>{money(catalog.currency, vatAmount)}</span>
                  </div>
                )}
                <div className="storefront__product-head storefront__total">
                  <span>{t('orderTotal')}</span>
                  <span>{money(catalog.currency, total)}</span>
                </div>
              </div>
            )}

            {/* Voucher — apply to see its effect on the total before paying. Above the address
                so the price is settled first. The order re-validates it server-side at checkout. */}
            <Field label={t('voucherCode')}>
              <div className="d-flex" style={{ gap: '0.5rem' }}>
                <input className="auth__input" type="text" value={voucher}
                       onChange={onVoucherChange}
                       placeholder={t('voucherCodePlaceholder')} />
                <button type="button" className="bravos-submit bravos-submit--ghost"
                        style={{ marginTop: 0, width: 'auto', flexShrink: 0, padding: '0 18px' }}
                        onClick={applyVoucher}
                        disabled={voucherLoading || !voucher.trim() || !product}>
                  {voucherLoading ? <span className="auth__spinner" /> : t('applyVoucher')}
                </button>
              </div>
            </Field>
            {prefilled && voucherApplied && (
              <p className="bravos-q__hint" style={{ color: '#1a7f4b' }}>{t('voucherPreApplied')}</p>
            )}
            {discount > 0 && (
              <p className="bravos-q__hint" style={{ color: '#1a7f4b' }}>
                {t('voucherApplied')} · −{money(catalog.currency, discount)}
              </p>
            )}
            {voucherError && <p className="text-danger mb-2">{voucherError}</p>}

            {/* Billing address (pre-filled from the member's last booking; saved for next time). */}
            <div className="auth__row">
              <Field label={t('street')}>
                <input className="auth__input" type="text" value={addr.street}
                       onChange={setAddrField('street')} autoComplete="address-line1" required />
              </Field>
              <Field label={t('number')}>
                <input className="auth__input" type="text" value={addr.number}
                       onChange={setAddrField('number')} />
              </Field>
            </div>
            <div className="auth__row">
              <Field label={t('zip')}>
                <input className="auth__input" type="text" value={addr.zip}
                       onChange={setAddrField('zip')} autoComplete="postal-code" required />
              </Field>
              <Field label={t('city')}>
                <input className="auth__input" type="text" value={addr.city}
                       onChange={setAddrField('city')} autoComplete="address-level2" required />
              </Field>
            </div>
            <Field label={t('country')}>
              <input className="auth__input" type="text" value={addr.country}
                     onChange={setAddrField('country')} autoComplete="country-name" required />
            </Field>

            {catalog.terms_url && (
              <label className="auth__hint d-flex align-items-start" style={{ gap: '0.5rem' }}>
                <input type="checkbox" checked={terms}
                       onChange={(e) => setTerms(e.target.checked)} required />
                <span>
                  {t('iAcceptThe')}{' '}
                  <a href={catalog.terms_url} target="_blank" rel="noreferrer">
                    {t('termsAndConditions')}
                  </a>
                </span>
              </label>
            )}

            {error && <p className="text-danger">{error}</p>}

            <button type="submit" className="bravos-submit" disabled={submitting || !product}>
              {submitting ? <span className="auth__spinner" /> : <Icon name="credit-card" />}
              {t('payAndBook')}
            </button>
          </form>
        )}
      </Card>
    </Base>
  );
};

export default MemberCheckout;
